/**
 * Core domain types — the multi-tenant foundation.
 *
 * Every domain entity belongs to an organization. The `organizationId` field is
 * mandatory on every domain table and every API query is scoped by it. This
 * lets us cleanly add new business units (KWPL, KWH, future ones) without
 * schema rewrites and gives us strict data isolation as the team grows.
 */

// ── Roles ──────────────────────────────────────────────────────────────
// Mirrors the EOS role taxonomy plus the compliance seats (Phase 5a).
// MUST stay in lockstep with the server's `SystemRole` Prisma enum —
// pages and CommandK compare against these strings.
export type SystemRole =
  | 'visionary'
  | 'integrator'
  | 'department_lead'
  | 'employee'
  | 'quality_lead'
  | 'qa_personnel'
  | 'auditor_readonly';

export const SYSTEM_ROLES: SystemRole[] = [
  'visionary',
  'integrator',
  'department_lead',
  'employee',
  'quality_lead',
  'qa_personnel',
  'auditor_readonly',
];

export function isSystemRole(value: unknown): value is SystemRole {
  return typeof value === 'string' && (SYSTEM_ROLES as string[]).includes(value);
}

// ── Organization ───────────────────────────────────────────────────────
export interface Organization {
  id: string;
  name: string;
  slug: string;
  /**
   * Per-org branding. Falls back to platform defaults if any key is missing.
   * Stored as a single JSON column server-side.
   */
  branding: {
    displayName?: string;
    logoUrl?: string;
    primaryColor?: string; // CSS color value
  };
  /** Industry template applied at sign-up — drives default departments + metrics. */
  industryTemplate: 'manufacturing' | 'saas' | 'services' | 'ecommerce' | 'agency' | 'custom';
  createdAt: string;
}

// ── User + Membership ──────────────────────────────────────────────────
// A User can belong to multiple Organizations through Memberships. Each
// Membership carries the role + department assignment for that specific org.
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  createdAt: string;
}

export interface Membership {
  id: string;
  organizationId: string;
  userId: string;
  role: SystemRole;
  departmentId?: string | null;
  invitedByMembershipId?: string | null;
  joinedAt: string;
}

// ── Auth state ─────────────────────────────────────────────────────────
// Returned by `/api/auth/me` once Phase 2 ships. Phase 1 only stubs this.
export interface AuthState {
  user: User | null;
  organization: Organization | null;
  membership: Membership | null;
  /** Set when the user is previewing as another role/membership (Phase 2). */
  impersonation: {
    viewAsRole?: SystemRole;
    viewAsMembershipId?: string;
    expiresAt: string;
  } | null;
}
